import React from 'react';
import { PublicLayout } from '@/components/PublicLayout';
import { useLanguage } from '@/contexts/LanguageContext';
import { Scale, Building2, Globe, FileText, AlertCircle, Gavel } from 'lucide-react';

const LegalNotice = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: 'Mentions légales',
      lastUpdate: 'Dernière mise à jour : Janvier 2026',
      sections: [
        {
          icon: Building2,
          title: '1. Informations légales',
          content: `Raison sociale : AILE - Aide à l'Intégration et au Logement des étudiants
Forme juridique : Association loi 1901
Siège social : 1 place Abbé Pierre, 69009 Lyon, France

Directeur de la publication : Président de l'association AILE
Contact : contact@aile-france.fr
Téléphone : +33 6 81 65 25 38`
        },
        {
          icon: Globe,
          title: '2. Hébergement',
          content: `Le site www.aile-france.fr est hébergé par :
webysee.com
Adresse : [Adresse de l'hébergeur]
Contact : contact@aile-france.fr`
        },
        {
          icon: FileText,
          title: '3. Propriété intellectuelle',
          content: `L'ensemble du contenu de ce site (textes, images, logos, graphismes, icônes, etc.) est la propriété exclusive de l'association AILE ou de ses partenaires, et est protégé par les lois françaises et internationales relatives à la propriété intellectuelle.

Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation écrite préalable de l'association AILE.

Toute exploitation non autorisée du site ou de l'un quelconque des éléments qu'il contient sera considérée comme constitutive d'une contrefaçon et poursuivie conformément aux dispositions des articles L.335-2 et suivants du Code de Propriété Intellectuelle.`
        },
        {
          icon: AlertCircle,
          title: '4. Limitation de responsabilité',
          content: `L'association AILE s'efforce d'assurer au mieux de ses possibilités l'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, l'association AILE ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur ce site.

En conséquence, l'association AILE décline toute responsabilité :
• Pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur le site
• Pour tous dommages résultant d'une intrusion frauduleuse d'un tiers ayant entraîné une modification des informations mises à disposition sur le site
• Et plus généralement, pour tous dommages, directs ou indirects, qu'elles qu'en soient les causes, origines, natures ou conséquences

Les annonces de logement présentes sur le site sont fournies à titre indicatif. L'association AILE ne saurait être tenue responsable des informations fournies par les annonceurs.`
        },
        {
          icon: Scale,
          title: '5. Liens hypertextes',
          content: `Le site peut contenir des liens hypertextes vers d'autres sites présents sur le réseau Internet. Les liens vers ces autres ressources vous font quitter le site AILE.

L'association AILE n'est pas responsable du contenu des sites vers lesquels ces liens pointent et ne peut être tenue responsable des dommages ou pertes découlant de leur utilisation.

La création de liens hypertextes vers le site www.aile-france.fr est soumise à l'accord préalable de l'association AILE.`
        },
        {
          icon: Gavel,
          title: '6. Droit applicable',
          content: `Les présentes mentions légales sont régies par le droit français.

En cas de litige, et après l'échec de toute tentative de recherche d'une solution amiable, les tribunaux français seront seuls compétents pour connaître de ce litige.

Pour toute question relative aux présentes mentions légales, vous pouvez nous contacter à l'adresse : contact@aile-france.fr`
        }
      ]
    },
    en: {
      title: 'Legal Notice',
      lastUpdate: 'Last updated: January 2026',
      sections: [
        {
          icon: Building2,
          title: '1. Legal information',
          content: `Company name: AILE - Lyon Integration Association for Students
Legal form: Association under the French Law of 1901
Registered office: 1 place Abbé Pierre, 69009 Lyon, France

Publication director: President of the AILE association
Contact: contact@aile-france.fr
Phone: +33 6 81 65 25 38`
        },
        {
          icon: Globe,
          title: '2. Hosting',
          content: `The website www.aile-france.fr is hosted by:
webysee.com
Address: [Host address]
Contact: contact@aile-france.fr`
        },
        {
          icon: FileText,
          title: '3. Intellectual property',
          content: `All content on this site (texts, images, logos, graphics, icons, etc.) is the exclusive property of the AILE association or its partners, and is protected by French and international intellectual property laws.

Any reproduction, representation, modification, publication, adaptation of all or part of the elements of the site, by any means or process whatsoever, is prohibited without prior written authorization from the AILE association.

Any unauthorized use of the site or any of its elements will be considered as constituting infringement and will be prosecuted in accordance with the provisions of articles L.335-2 and following of the Intellectual Property Code.`
        },
        {
          icon: AlertCircle,
          title: '4. Limitation of liability',
          content: `The AILE association endeavors to ensure the accuracy and updating of the information published on this site to the best of its ability. However, the AILE association cannot guarantee the accuracy, precision or completeness of the information made available on this site.

Consequently, the AILE association declines all responsibility:
• For any inaccuracy, error or omission relating to information available on the site
• For any damage resulting from fraudulent intrusion by a third party leading to a modification of the information made available on the site
• And more generally, for any direct or indirect damage, whatever the causes, origins, nature or consequences

Housing listings on the site are provided for informational purposes only. The AILE association cannot be held responsible for information provided by advertisers.`
        },
        {
          icon: Scale,
          title: '5. Hyperlinks',
          content: `The site may contain hyperlinks to other sites on the Internet. Links to these other resources take you away from the AILE site.

The AILE association is not responsible for the content of sites to which these links point and cannot be held responsible for any damage or loss arising from their use.

The creation of hyperlinks to the site www.aile-france.fr is subject to prior agreement from the AILE association.`
        },
        {
          icon: Gavel,
          title: '6. Applicable law',
          content: `These legal notices are governed by French law.

In the event of a dispute, and after the failure of any attempt to find an amicable solution, the French courts will have sole jurisdiction to hear the dispute.

For any questions regarding these legal notices, you can contact us at: contact@aile-france.fr`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <PublicLayout>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#127abb]/10 rounded-2xl mb-6">
              <Scale className="text-[#127abb]" size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {t.title}
            </h1>
            <p className="text-slate-500">{t.lastUpdate}</p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <div 
                key={index} 
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#127abb]/10 rounded-xl flex items-center justify-center">
                    <section.icon className="text-[#127abb]" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-3">
                      {section.title}
                    </h2>
                    <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LegalNotice;
